import { useEffect, useReducer } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getRefresh, SourcingApiError, startRefresh } from "./api";
import { COPY, IDLE, POLL_MS, followVerdict, refreshReducer, type RefreshModel } from "./refreshFollow";

function localTime(value: string | number | null): string {
  if (value === null) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function outcomeFor(model: RefreshModel): { message: string; role?: "status" | "alert" } {
  if (model.state === "running") {
    if (model.pollFailed) return { message: COPY.waiting, role: "status" };
    const started = localTime(model.startedAt ?? model.pressAt);
    if (model.alreadyRunning) {
      return { message: `${COPY.already} (started ${started}). ${COPY.following}`, role: "status" };
    }
    return { message: `${COPY.running} ${started}`, role: "status" };
  }
  if (model.state === "done") return { message: `${COPY.done} ${localTime(model.builtAt)}`, role: "status" };
  if (model.state === "failed") return { message: COPY.failed, role: "alert" };
  if (model.state === "refused") {
    return { message: model.refusedStatus === 403 ? COPY.forbidden : COPY.refused, role: "alert" };
  }
  if (model.state === "cap") return { message: COPY.cap, role: "status" };
  return { message: "" };
}

export function useSourcingRefresh() {
  const queryClient = useQueryClient();
  const [model, dispatch] = useReducer(refreshReducer, IDLE);
  const refresh = useQuery({
    queryKey: ["sourcing_funnel", "refresh"],
    queryFn: getRefresh,
    staleTime: 60_000,
    refetchInterval: model.state === "starting" || model.state === "running" ? POLL_MS : false,
    retry: false,
  });

  useEffect(() => {
    const run = refresh.data?.run;
    if (run?.status === "running") dispatch({ type: "attach", run, now: Date.now() });
  }, [refresh.data, refresh.dataUpdatedAt]);

  useEffect(() => {
    if (model.state !== "running" || model.followStart === null) return;
    // Ignore the refresh answer cached before this press; only a later poll may
    // decide the new run. This matters when another run completed between the
    // cached answer and the baseline GET.
    if (Math.max(refresh.dataUpdatedAt, refresh.errorUpdatedAt) < model.followStart) return;
    const run = refresh.data?.run ?? null;
    const failed = refresh.isError;
    const now = Date.now();
    const verdict = followVerdict({
      run,
      failed,
      now,
      followStart: model.followStart,
      runId: model.runId,
      baselineRunId: model.baselineRunId,
    });
    dispatch({ type: "poll", run, failed, now });
    // The terminal dispatch leaves `running`, so this branch runs once per followed run.
    if (verdict === "ok") void queryClient.invalidateQueries({ queryKey: ["sourcing_funnel"] });
  }, [
    refresh.data,
    refresh.dataUpdatedAt,
    refresh.errorUpdatedAt,
    refresh.isError,
    model.state,
    model.followStart,
    model.runId,
    model.baselineRunId,
    queryClient,
  ]);

  async function start() {
    dispatch({ type: "press", now: Date.now() });
    const before = await getRefresh().catch(() => null);
    try {
      const answer = await startRefresh();
      if (answer.status === "started") {
        dispatch({ type: "started", baselineRunId: before?.run?.run_id ?? null });
      } else {
        dispatch({ type: "already", run: answer.run });
      }
    } catch (error) {
      dispatch({ type: "refused", status: error instanceof SourcingApiError ? error.status : 0 });
    }
  }

  const outcome = outcomeFor(model);
  return {
    state: model.state,
    message: outcome.message,
    role: outcome.role,
    busy: model.state === "starting" || model.state === "running",
    start,
  };
}
